import { useState, type FormEvent } from "react";
import { Button, Field, FormDialog, Skeleton, EmptyState, DialogCancel } from "@cntrl/ui";
import { formErrorMessage } from "../../../app/hooks/apiError.js";
import { useCapability } from "../../../app/bandContext.js";
import { useFanDetail, useUpdateFan, useDeleteFan, type FanView } from "../../../app/hooks/useFans.js";
import { MetroHintNote } from "./MetroHintNote.js";
import styles from "./FanForm.module.scss";

/**
 * The Fan Detail overlay â view + edit a fan's name/phone/city (with the live metro-rollup hint)/state/
 * country/shows-attended + subscription/opt-out status, plus read-only Activity (source, added date). Opted-out
 * fans render their status and are suppressed from any future send (by construction â v1 sends nothing). Never
 * optimistic; the inner form is seeded ONCE from the loaded fan (not keyed on dataUpdatedAt), so a background
 * refetch never wipes an in-progress edit (the draft-clobber lesson).
 */
export function FanDetailOverlay({ fanId, onClose }: { fanId: string; onClose: () => void }) {
  const q = useFanDetail(fanId);

  return (
    // ONE FormDialog across loading and loaded (a Modal on desktop, a bottom Sheet on a phone). The edit form, its
    // state and its two-step Remove live in the child, so the actions stay in the body rather than the footer slot:
    // lifting them out would mean lifting the form's state, or remounting the dialog when the fan loads.
    <FormDialog title="Fan detail" size="md" onClose={onClose}>
      {q.isLoading || !q.data ? (
        q.isError ? (
          <EmptyState title="Couldn't load this fan" description="Something went wrong." />
        ) : (
          <div role="status" aria-label="Loading">
            <Skeleton variant="line" width="70%" />
            <Skeleton variant="line" width="50%" />
          </div>
        )
      ) : (
        <FanDetailForm key={fanId} fan={q.data} onClose={onClose} />
      )}
    </FormDialog>
  );
}

function FanDetailForm({ fan, onClose }: { fan: FanView; onClose: () => void }) {
  const canManage = useCapability("fans:manage");
  const update = useUpdateFan();
  const del = useDeleteFan();

  const [firstName, setFirstName] = useState(fan.firstName ?? "");
  const [lastName, setLastName] = useState(fan.lastName ?? "");
  const [phone, setPhone] = useState(fan.phone ?? "");
  const [city, setCity] = useState(fan.city ?? "");
  const [state, setState] = useState(fan.state ?? "");
  const [country, setCountry] = useState(fan.country ?? "");
  const [showsAttended, setShowsAttended] = useState(String(fan.showsAttended ?? 0));
  const [emailSubscribed, setEmailSubscribed] = useState(fan.emailSubscribed ?? true);
  const [smsOptIn, setSmsOptIn] = useState(fan.smsOptIn ?? false);
  const [optedOut, setOptedOut] = useState(fan.optedOut ?? false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fanId = fan.fanId!;

  function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    update.mutate(
      {
        fanId,
        body: {
          firstName: firstName || null,
          lastName: lastName || null,
          phone: phone || null,
          city: city || null,
          state: state || null,
          country: country || null,
          showsAttended: Math.max(0, Number.parseInt(showsAttended, 10) || 0),
          emailSubscribed,
          smsOptIn,
          optedOut,
        },
      },
      { onSuccess: onClose, onError: (err) => setError(formErrorMessage(err, "Couldn't save")) },
    );
  }

  const readOnly = !canManage;

  return (
    <form className={styles.form} onSubmit={save}>
      <div className={styles.grid2}>
        <Field label="First name" value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} disabled={readOnly} />
        <Field label="Last name" value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} disabled={readOnly} />
      </div>
      <Field label="Phone" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} disabled={readOnly} />
      <div className={styles.grid2}>
        <Field label="City" value={city} onChange={(e) => setCity(e.currentTarget.value)} disabled={readOnly} />
        <Field label="State" value={state} onChange={(e) => setState(e.currentTarget.value)} disabled={readOnly} />
      </div>
      <MetroHintNote city={city} state={state} />
      <div className={styles.grid2}>
        <Field label="Country" value={country} onChange={(e) => setCountry(e.currentTarget.value)} disabled={readOnly} />
        <Field label="Shows attended" type="number" min={0} value={showsAttended} onChange={(e) => setShowsAttended(e.currentTarget.value)} disabled={readOnly} />
      </div>

      <label className={styles.check}>
        <input type="checkbox" checked={emailSubscribed} onChange={(e) => setEmailSubscribed(e.currentTarget.checked)} disabled={readOnly} />
        Subscribed to email
      </label>
      <label className={styles.check}>
        <input type="checkbox" checked={smsOptIn} onChange={(e) => setSmsOptIn(e.currentTarget.checked)} disabled={readOnly} />
        Opted in to SMS
      </label>
      <label className={styles.check}>
        <input type="checkbox" checked={optedOut} onChange={(e) => setOptedOut(e.currentTarget.checked)} disabled={readOnly} />
        Opted out (suppressed from all sends)
      </label>

      {/* Activity (read-only) */}
      <div className={styles.activity} aria-label="Activity">
        <div className={styles.activityRow}><span className={styles.activityKey}>Source</span><span>{fan.source}</span></div>
        <div className={styles.activityRow}><span className={styles.activityKey}>Shows attended</span><span>{fan.showsAttended ?? 0}</span></div>
        <div className={styles.activityRow}><span className={styles.activityKey}>Added</span><span>{fan.createdOn ? new Date(fan.createdOn).toLocaleDateString() : "—"}</span></div>
        {fan.email && <div className={styles.activityRow}><span className={styles.activityKey}>Email</span><span>{fan.email}</span></div>}
        {optedOut && <p className={styles.optOutTag}>This fan has opted out</p>}
      </div>

      {error && <p className={styles.formError} role="alert">{error}</p>}

      {canManage && (
        <div className={styles.actions}>
          {confirmDelete ? (
            <span className={styles.check}>
              Remove this fan?
              <Button type="button" variant="accent" loading={del.isPending}
                onClick={() => del.mutate({ fanId }, { onSuccess: onClose })}>
                Remove
              </Button>
              <Button type="button" variant="link" onClick={() => setConfirmDelete(false)}>Keep</Button>
            </span>
          ) : (
            <Button type="button" variant="link" onClick={() => setConfirmDelete(true)}>Remove fan</Button>
          )}
          <span className={styles.actionsEnd}>
            <DialogCancel />
            <Button type="submit" variant="primary" loading={update.isPending}>Save</Button>
          </span>
        </div>
      )}
    </form>
  );
}
